import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { getBdcDashboardSummary } from "../../../../services/bdcDashboard";
import {
  BdcDashboardGranularity,
  BdcDashboardSummary,
  BdcDashboardSummaryParams,
} from "../../../../types/bdcDashboard.type";
import { todayBkk } from "../../format";
import { useBdcDcs } from "../../useBdcDcs";
import { bdcDashboardStoresLink, GRANULARITY_OPTIONS } from "../constants";
import { ALL, BranchSelect, DateField, DcSelect, dcsForBranch } from "../Filters";
import { dashboardErrorMessage, periodLabel } from "../format";
import { useBdcBranches } from "../useBdcBranches";
import SummaryView from "./SummaryView";

const isGranularity = (value: string | null): value is BdcDashboardGranularity =>
  GRANULARITY_OPTIONS.some((option) => option.value === value);

/**
 * BDC · ยอดสั่ง / ยอดส่ง — the GM's daily / weekly / monthly read of 7-11 orders and
 * deliveries. Filters live in the URL so a link or the back button returns to the same view.
 */
export default function BdcDashboardSummaryScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { dcs } = useBdcDcs();
  const branches = useBdcBranches();

  const date = params.get("date") || todayBkk();
  const rawGranularity = params.get("granularity");
  const granularity: BdcDashboardGranularity = isGranularity(rawGranularity) ? rawGranularity : "day";
  const dcCode = params.get("dc_code") || ALL;
  const companyId = params.get("company_id") || ALL;

  const request = useMemo<BdcDashboardSummaryParams>(
    () => ({
      date,
      granularity,
      ...(dcCode !== ALL ? { dc_code: dcCode } : {}),
      ...(companyId !== ALL ? { company_id: companyId } : {}),
    }),
    [date, granularity, dcCode, companyId],
  );

  const [summary, setSummary] = useState<BdcDashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await getBdcDashboardSummary(request);
      setSummary(data);
    } catch (err) {
      setSummary(null);
      setError(dashboardErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === ALL) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const openStores = () => {
    const link = bdcDashboardStoresLink(summary?.current.as_of ?? date);
    const extra = new URLSearchParams();
    if (dcCode !== ALL) extra.set("dc_code", dcCode);
    if (companyId !== ALL) extra.set("company_id", companyId);
    navigate(extra.toString() ? `${link}&${extra.toString()}` : link);
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              BDC · ยอดสั่ง / ยอดส่ง
            </Typography>
            {summary && (
              <Typography variant="body2" color="text.secondary">
                {periodLabel(granularity, summary.period.from, summary.period.to)} · ตามวันส่ง · ไม่รวม PO
                แบบไม่มีรายร้าน
              </Typography>
            )}
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={granularity}
              onChange={(_, value: BdcDashboardGranularity | null) => value && update({ granularity: value })}
              aria-label="ช่วงเวลา"
            >
              {GRANULARITY_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value} sx={{ px: 1.75 }}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <DateField label="วันที่" value={date} onChange={(value) => update({ date: value })} />
            <BranchSelect
              branches={branches}
              value={companyId}
              onChange={(value) => update({ company_id: value })}
            />
            <DcSelect
              dcs={dcsForBranch(dcs, branches, companyId)}
              value={dcCode}
              onChange={(value) => update({ dc_code: value })}
            />
          </Stack>
        </Stack>
      </LocalizationProvider>

      {loading && <LinearProgress sx={{ mb: 1.5 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {summary && <SummaryView summary={summary} branches={branches} onOpenStores={openStores} />}
    </Box>
  );
}
